<template>
  <div
    data-tour="ai-prompt"
    class="relative flex h-full flex-col justify-between"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <DropZoneOverlay :visible="isDragging" />

    <div class="flex min-h-0 flex-col gap-3 overflow-y-auto p-3">
      <div>
        <label class="mb-1 block text-xs font-semibold text-gray-700">
          What would you like to know or do?
        </label>
        <textarea
          v-model="prompt"
          rows="3"
          class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Example: Analyze the top 5 campaigns by cost and suggest which ones to optimize"
          :disabled="isSubmitting || isTokenLimitExceeded"
          @keydown.enter.exact.prevent="handleSubmit"
          @paste="handlePaste"
        />
      </div>

      <PendingAttachmentList
        :attachments="pendingAttachments"
        :warning="attachmentWarning"
        @remove="removePendingAttachment"
      />

      <div
        v-if="isTokenLimitExceeded"
        class="rounded-md bg-orange-50 p-3 text-sm text-orange-800"
      >
        <p class="font-semibold">Monthly AI usage limit reached</p>
        <p>
          Your organization has used its monthly AI allowance. The limit will
          reset in {{ usage.data?.daysUntilRenewal }}
          {{ usage.data?.daysUntilRenewal === 1 ? "day" : "days" }}. Please
          contact your administrator for more information.
        </p>
      </div>

      <div v-if="visibleSuggestions.length > 0 && !isTokenLimitExceeded">
        <p class="mb-1.5 text-xs text-gray-600">Quick suggestions:</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="suggestion in visibleSuggestions"
            :key="suggestion"
            class="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isSubmitting"
            @click="prompt = suggestion"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- The panel stays usable before consent so people can see what the
        feature does; the consent step happens when they try to send. -->
      <AIChatConsentNotice
        v-if="isConsentPending"
        :consent-key="AI_CHAT_CONSENT_KEY"
        :is-manager="role.isManager.value"
        :is-member="role.isMember.value"
        :role-failed="role.isError.value"
        :attention="consentAttention"
        @review-terms="void openConsentModal(false)"
        @retry-role="void role.refetch()"
      />

      <div
        v-if="createSessionMutation.error"
        class="rounded-md bg-red-50 p-3 text-sm text-red-800"
      >
        <p class="font-semibold">Error:</p>
        <p>
          {{ errorMessage || "Failed to start AI agent session" }}
        </p>
      </div>

      <div v-if="recentSessions.length > 0">
        <p class="mb-2 text-xs text-gray-600">Recent chats:</p>
        <div class="space-y-1">
          <ChatHistoryItem
            v-for="session in recentSessions"
            :key="session.id"
            :session-id="session.id"
            :title="session.title"
            row-class="gap-3 rounded-md px-3 py-2"
            button-class="hover:bg-gray-100"
            @select="resumeSession"
          >
            <Icon name="comment-dots" class="size-4 shrink-0 text-gray-400" />
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-gray-700">
                {{ session.title }}
              </p>
              <p class="text-xs text-gray-400">
                {{ formatRelativeTime(session.modifiedAt) }}
              </p>
            </div>
            <Icon name="angle-right" class="size-3.5 shrink-0 text-gray-300" />
          </ChatHistoryItem>
        </div>
      </div>
    </div>

    <div
      class="sticky bottom-0 flex shrink-0 flex-col gap-3 border-t border-gray-200 bg-white p-3"
    >
      <!-- No session exists yet, so the levels come from the organization's
        default model — the same question the session payload answers, asked
        of the model the session about to be created will run on. Showing the
        full set before that answer arrives offers `Minimal` to an org on
        gpt-5.6-luna and then drops a position out from under the handle,
        which is the mismatch this control exists to remove.

        Three states, not two: an empty `supportedEfforts` means both "no
        answer yet" and "this model takes no effort setting at all". Until the
        answer lands the control renders but stays hidden, so
        the button row below does not jump when the answer lands — and a
        `visibility: hidden` copy of the real control holds exactly the right
        height, which a stand-in with a hard-coded one stops doing the moment
        this control grows a row. It is out of the tab order and the
        accessibility tree meanwhile. Only the first open waits; the query is
        cached for the page. -->
      <ReasoningEffortSelect
        v-if="showEffortControl && !isConsentPending"
        v-model="chatEffort"
        :options="servedEfforts"
        :disabled="isSubmitting || isTokenLimitExceeded"
        :class="{ invisible: agentConfig.isPending }"
      />
      <div class="flex items-center justify-end gap-2">
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />
        <button
          type="button"
          class="rounded-md p-2 text-gray-400 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isSubmitting || isTokenLimitExceeded"
          title="Attach image"
          @click="fileInput?.click()"
        >
          <Icon name="image" class="size-5" />
        </button>
        <button
          class="flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="
            (!prompt.trim() && pendingAttachments.length === 0) ||
            isSubmitting ||
            isTokenLimitExceeded
          "
          @click="handleSubmit"
        >
          <Icon
            v-if="isSubmitting"
            name="spinner-third-duotone"
            class="size-4 animate-spin"
          />
          <span>Send to Automagic AI</span>
        </button>
      </div>
    </div>

    <!-- Teleported: mounted inside this flex column, the open dialog's nodes
      became a third item and `justify-between` pushed the Send row up. -->
    <Teleport to="body">
      <ConsentDetailModal
        v-if="isConsentPending"
        ref="consentModal"
        :is-manager="role.isManager.value"
        @confirm-consent="handleConsentGiven"
        @dismiss="armedSend = null"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from "vue";
import { useRoute } from "vue-router";
import ReasoningEffortSelect from "@/components/AIAgent/ReasoningEffortSelect.vue";
import Icon from "@/components/Core/Icon.vue";
import ChatHistoryItem from "@/components/AIAgent/ChatHistoryItem.vue";
import DropZoneOverlay from "@/components/AIAgent/DropZoneOverlay.vue";
import PendingAttachmentList from "@/components/AIAgent/PendingAttachmentList.vue";
import {
  useAIAgentConfigQuery,
  useCreateAISessionMutation,
  useAISessionsQuery,
  type ChatAttachment,
} from "@/services/ai_agent";
import { getApiErrorMessage } from "@/composables/useAIAgentStream";
import {
  reconcileEffortWithSession,
  useAiChatSession,
} from "@/composables/useAiChatSession";
import { useIsAITokenLimitExceeded } from "@/services/ai_token_usage";
import {
  usePendingAttachments,
  IMAGE_ONLY_FALLBACK_MESSAGE,
} from "@/composables/usePendingAttachments";
import { DateTime } from "luxon";
import AIChatConsentNotice from "@/components/AIAgent/AIChatConsentNotice.vue";
import ConsentDetailModal from "@/views/OrganizationSettings/ConsentDetailModal.vue";
import { useFeatureGate } from "@/services/feature_gate";
import { useOrganizationRole } from "@/services/user";

const AI_CHAT_CONSENT_KEY = "ai_chat";

const {
  pendingPrompt,
  chatEffort,
  draft: prompt,
  draftAttachments,
  isDraftSubmitting,
  startDraftSubmit,
  resumeSession,
  handleSessionCreated,
} = useAiChatSession();

const {
  pendingAttachments,
  attachmentWarning,
  isDragging,
  handleFileSelect,
  handlePaste,
  onDragOver,
  onDragLeave,
  onDrop,
  removePendingAttachment,
  uploadPendingAttachments,
  releasePendingAttachments,
} = usePendingAttachments(draftAttachments);

const fileInput = ref<HTMLInputElement | null>(null);
const route = useRoute();

// Keep in sync with _COMPETITOR_VIEW_NAMES in
// backend/ai_agents/engine/agent_loop.py
const COMPETITOR_VIEW_NAMES = new Set([
  "competitor-monitor",
  "competitor-overview",
  "competitor-posts",
  "competitor-post",
  "competitors",
  "share-of-search",
]);

const DEFAULT_SUGGESTIONS = [
  "What are my top traffic sources?",
  "Show me conversion trends for the last 30 days",
  "Give me a summary across all channels",
  "Show me metrics that need attention",
  "Analyze recent performance trends",
  "What are the key takeaways from my latest data?",
  "Check if any of current local news is relevant to my campaigns. Assert impact severity and likelihood of impact. Include only news that are relevant to my campaigns.",
  "Create a dashboard with an overview of all my ad campaigns",
  "Break down my spending by channel and highlight any budget anomalies",
  "Which keywords are driving conversions but have a below-average quality score?",
  "Compare this month's ROAS to last month across all platforms and suggest where to reallocate budget",
];

const COMPETITOR_SUGGESTIONS = [
  "How are my competitors performing on social media?",
  "Compare my follower count against all competitors",
  "Show me the top competitor posts by engagement",
  "What are my competitors' posting patterns?",
  "Which competitor is growing followers the fastest?",
  "Show me Share of Search trends over the last 3 months",
  "Which keywords do my competitors dominate in search?",
  "Create a competitor monitoring dashboard",
  "Compare my posting frequency to competitors",
  "Show me which competitor posts got the most comments recently",
];

const suggestions = computed(() =>
  COMPETITOR_VIEW_NAMES.has(route.name as string)
    ? COMPETITOR_SUGGESTIONS
    : DEFAULT_SUGGESTIONS,
);

const MAX_SUGGESTIONS = 6;
const visibleSuggestions = computed(() =>
  suggestions.value.slice(0, MAX_SUGGESTIONS),
);

const agentConfig = useAIAgentConfigQuery();

// Served rather than derived from the model name, for the reason the chat
// panel's copy gives: the effort vocabulary differs within the GPT-5 family.
const supportedEfforts = computed(
  () => agentConfig.data?.supportedReasoningEfforts ?? [],
);

// A failed fetch with nothing cached falls back to the control's own full set
// (`undefined` means "every offered level" to it). That is the guess this PR
// removed, reinstated only where the alternative is losing a capability the
// user had before, with no message and no retry: the backend still clamps a
// level the model rejects, so the worst case here is the old one rather than
// a new one. A failed background refetch keeps the cached answer, and that
// answer still wins: TanStack sets `isError` alongside retained `data`.
const hasServedAnswer = computed(() => agentConfig.data !== undefined);
const servedEfforts = computed(() =>
  !hasServedAnswer.value && agentConfig.isError
    ? undefined
    : supportedEfforts.value,
);

// `isPending` rather than `isLoading`: a query still disabled by the feature
// gate is pending-but-not-loading, and that is the front half of a cold open
// (flags query, then config query). Reserving only the second half left the
// button row shifting on exactly the case the reserved space is for.
const showEffortControl = computed(
  () =>
    agentConfig.isPending ||
    servedEfforts.value === undefined ||
    supportedEfforts.value.length > 0,
);
const createSessionMutation = useCreateAISessionMutation();
const { isExceeded: isTokenLimitExceeded, usage } = useIsAITokenLimitExceeded();
const sessions = useAISessionsQuery();

const MAX_RECENT = 3;

// Hidden while consent is pending: a lapsed consent leaves the cached list in
// place, and resuming an old chat from here would swap the panel holding the
// draft for that chat as soon as a manager accepts.
const recentSessions = computed(() => {
  if (!sessions.data || isConsentPending.value) return [];
  return sessions.data.slice(0, MAX_RECENT);
});

function formatRelativeTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toRelative() ?? "";
}

const isUploading = ref(false);
const isSubmitting = computed(
  () =>
    isUploading.value ||
    createSessionMutation.isPending ||
    isDraftSubmitting.value,
);

const errorMessage = computed(() =>
  createSessionMutation.error
    ? getApiErrorMessage(createSessionMutation.error)
    : null,
);

const aiChatGate = useFeatureGate("aiChat");
const isConsentPending = computed(() => aiChatGate.isConsentPending.value);
const role = useOrganizationRole();
const consentModal = ref<InstanceType<typeof ConsentDetailModal> | null>(null);
const consentAttention = ref(0);
// Send while consent is pending arms a send of *that* question for when the
// gate unlocks: after "I Agree", or when the refetch below finds another
// manager already consented. The armed send is the draft as it was at Send —
// an edit made meanwhile was never sent, so it cancels rather than going to
// the AI provider unasked. Time-boxed too, so an unlock that lands much later
// (a failed refetch retried on focus) doesn't fire. "Review terms" and closing
// the terms arm nothing: that manager isn't sending.
const SEND_AFTER_CONSENT_WINDOW_MS = 60_000;
interface ArmedSend {
  prompt: string;
  attachments: readonly unknown[];
  until: number;
}
const armedSend = ref<ArmedSend | null>(null);

function armSend(): void {
  armedSend.value = {
    prompt: prompt.value,
    attachments: [...pendingAttachments.value],
    until: Date.now() + SEND_AFTER_CONSENT_WINDOW_MS,
  };
}

function draftUnchangedSince(armed: ArmedSend): boolean {
  const current = pendingAttachments.value;
  return (
    prompt.value === armed.prompt &&
    current.length === armed.attachments.length &&
    current.every((attachment, i) => attachment === armed.attachments[i])
  );
}

async function openConsentModal(fromSend: boolean): Promise<void> {
  if (fromSend) {
    armSend();
    // The gate may be stale (consent given in another tab or by another
    // manager); without this the modal shows "Consented" with nothing to do.
    aiChatGate.refetch();
  } else {
    armedSend.value = null;
  }
  // A prompt handed over by `openWithPrompt` submits from the immediate
  // watcher, during setup, before the modal's ref is bound.
  await nextTick();
  consentModal.value?.openModal(AI_CHAT_CONSENT_KEY);
}

function handleConsentGiven(): void {
  // Reading the terms can take longer than the window. Only an armed send is
  // extended: a dismissal while "I Agree" was in flight already cleared it.
  if (armedSend.value) {
    armedSend.value = {
      ...armedSend.value,
      until: Date.now() + SEND_AFTER_CONSENT_WINDOW_MS,
    };
  }
}

// Not straight after the mutation: session creation is refused until the
// flags refetch the mutation triggers lands and the gate unlocks.
watch(
  () => aiChatGate.isUnlocked.value,
  (unlocked) => {
    if (!unlocked) return;
    const armed = armedSend.value;
    armedSend.value = null;
    if (armed && Date.now() <= armed.until && draftUnchangedSince(armed)) {
      void handleSubmit();
    }
  },
);

// A handed-over prompt submits on arrival, so it waits until Send's consent
// branch can be decided: an unresolved gate would try to create a session the
// backend refuses, and an unresolved role would treat a manager as a member.
const canDecideSend = computed(
  () =>
    !aiChatGate.isLoading.value &&
    (!aiChatGate.isConsentPending.value || role.isResolved.value),
);

watch(
  [pendingPrompt, canDecideSend],
  ([newPrompt, ready]) => {
    if (!newPrompt || !ready) return;
    // Consumed at once: left queued, a blocked submit would re-fire on the
    // next mount and overwrite whatever the user typed since.
    pendingPrompt.value = null;
    prompt.value = newPrompt;
    handleSubmit();
  },
  { immediate: true },
);

async function handleSubmit() {
  // Captured up front for the same reason as AIChatPanel's handleSend.
  const submittedDraft = prompt.value;
  const submittedAttachments = [...pendingAttachments.value];
  const hasText = submittedDraft.trim().length > 0;
  const hasAttachments = submittedAttachments.length > 0;
  if (
    (!hasText && !hasAttachments) ||
    isSubmitting.value ||
    isTokenLimitExceeded.value
  ) {
    return;
  }

  if (isConsentPending.value) {
    // Unresolved role (a failed profile request): neither the terms nor a
    // request would be the right answer, and the notice offers the retry.
    if (role.isManager.value) {
      void openConsentModal(true);
    } else if (role.isMember.value) {
      consentAttention.value += 1;
    }
    return;
  }

  const submission = startDraftSubmit();
  try {
    // Upload attachments first
    let attachments: ChatAttachment[] | undefined;
    if (hasAttachments) {
      isUploading.value = true;
      const uploaded = await uploadPendingAttachments();
      isUploading.value = false;
      if (!uploaded) return; // upload failed
      attachments = uploaded;
    }

    const userPrompt = submittedDraft.trim() || IMAGE_ONLY_FALLBACK_MESSAGE;

    // Create session
    const session = await createSessionMutation.mutateAsync({
      title: userPrompt.substring(0, 50),
    });

    releasePendingAttachments(submittedAttachments);
    // The user moved on to another chat while this one was being created.
    if (!submission.isCurrent()) return;
    prompt.value = "";

    // Before the handoff, not after: the chat panel sends the initial message
    // as it mounts, reading the shared value directly rather than waiting for
    // its selector to render and normalise it.
    reconcileEffortWithSession(session.supportedReasoningEfforts);

    // Through shared state, not an emit: this panel may have unmounted
    // mid-submit, and Vue drops emits from unmounted instances.
    handleSessionCreated(session.id, userPrompt, attachments);
  } catch {
    // Error is displayed via createSessionMutation.error in the template
  } finally {
    submission.end();
  }
}
</script>
